import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getItems, deleteItem } from "../services/firestore";
import { useAuth } from "../contexts/AuthContext";

import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

import {
  exportItemsToExcel,
  downloadItemsTemplate,
  analyzeItemsExcel,
  importItemsFromExcel
} from "../services/excelService";

import {
  MoreVertical,
  Pencil,
  Trash2,
  Copy,
  Download,
  Upload,
  FileSpreadsheet
} from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "../components/ui/dropdown-menu";

import { toast } from "sonner";

export const ItemsPage = () => {

  const navigate = useNavigate();
  const { userData } = useAuth();

  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);

  const [search, setSearch] = useState("");
  const [brandFilter, setBrandFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");

  const handleExport = async () => {
  try {
    await exportItemsToExcel();
    toast.success("Excel downloaded");
  } catch (err) {
    toast.error("Export failed");
  }
};

  // const [importOpen, setImportOpen] = useState(false); // future ready

  /* =====================
     FETCH
  ===================== */

  useEffect(() => {
    fetchItems();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [search, brandFilter, categoryFilter, items]);

  const fetchItems = async () => {
    const data = await getItems();
    setItems(data);
    setFilteredItems(data);
  };

  /* =====================
     FILTER LOGIC
  ===================== */

  const applyFilters = () => {

    let result = [...items];

    if (search) {
      const q = search.toLowerCase();
      result = result.filter(i =>
        i.name?.toLowerCase().includes(q) ||
        i.code?.toLowerCase().includes(q)
      );
    }

    if (brandFilter !== "all") {
      result = result.filter(i => i.brand === brandFilter);
    }

    if (categoryFilter !== "all") {
      result = result.filter(i => i.category === categoryFilter);
    }

    setFilteredItems(result);
  };

  /* =====================
     UNIQUE FILTER VALUES
  ===================== */

  const brands = [...new Set(items.map(i => i.brand).filter(Boolean))];
  const categories = [...new Set(items.map(i => i.category).filter(Boolean))];

  /* =====================
     DELETE
  ===================== */

  const handleDelete = async (item) => {
    await deleteItem(item.id);
    toast.success("Deleted");
    fetchItems();
  };

  /* =====================
     COPY ITEM
  ===================== */

  const handleCopy = (item) => {
    navigate("/items/new", {
      state: { copyItem: item }
    });
  };

  /* =====================
     ADMIN ACTIONS
  ===================== */

const handleDownloadTemplate = () => {
  downloadItemsTemplate();
};

const handleImport = async (file) => {
  if (!file) return;

  try {

    const analysis = await analyzeItemsExcel(file);

    const shouldProceed = window.confirm(
      `Total: ${analysis.total}
Update: ${analysis.updateCount}
Create: ${analysis.createCount}

Proceed?`
    );

    if (!confirm) return;
      if (!analysis.total) {
      toast.error("No valid rows found");
      return;
      }

    const loadingToast = toast.loading("Importing...");

    await importItemsFromExcel(analysis.rows);

    toast.dismiss(loadingToast);
    toast.success("Import completed");

    fetchItems();

  } catch (err) {
    console.error(err);
    toast.error(err.message || "Import failed");
  }
};

  /* =====================
     UI
  ===================== */

  return (

    <div className="p-6 space-y-4">

      {/* HEADER */}

      <div className="flex justify-between items-center flex-wrap gap-3">

        <h1 className="text-2xl font-bold">Items</h1>

        <div className="flex gap-2 flex-wrap">

          {/* ADMIN ONLY CONTROLS */}
          {userData?.role === "admin" && (
            <>
              <Button variant="outline" onClick={handleDownloadTemplate}>
                <FileSpreadsheet className="mr-2 h-4 w-4" />
                Template
              </Button>

              <Button variant="outline" onClick={handleExport}>
                <Download className="mr-2 h-4 w-4" />
                Export
              </Button>

              <Button variant="outline" 
               onClick={() => document.getElementById("item-import-input").click()}>
                <Upload className="mr-2 h-4 w-4" />
                Import
              </Button>
            </>
          )}

          {/* ADD ITEM (ALWAYS VISIBLE) */}
          <Button onClick={() => navigate("/items/new")}>
            Add Item
          </Button>

<input
  type="file"
  accept=".xlsx, .xls"
  id="item-import-input"
  style={{ display: "none" }}
  onChange={(e) => {
  const file = e.target.files[0];
  if (file) handleImport(file);
  e.target.value = null; // important fix
}}
/>

        </div>

      </div>

      {/* FILTER BAR */}

      <div className="flex gap-3 flex-wrap">

        <Input
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          value={brandFilter}
          onChange={(e) => setBrandFilter(e.target.value)}
          className="border rounded p-2"
        >
          <option value="all">All Brands</option>
          {brands.map(b => <option key={b}>{b}</option>)}
        </select>

        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="border rounded p-2"
        >
          <option value="all">All Categories</option>
          {categories.map(c => <option key={c}>{c}</option>)}
        </select>

      </div>

      {/* LIST */}

      <div className="grid gap-4">

        {filteredItems.map(item => (

          <Card key={item.id}>
            <CardContent className="p-4 flex justify-between items-center">

              <div>
                <p className="font-semibold">{item.name}</p>
                <p className="text-sm text-slate-500">
                  {item.brand} • {item.category}
                </p>
              </div>

              <DropdownMenu>

                <DropdownMenuTrigger asChild>
                  <Button variant="ghost">
                    <MoreVertical size={16} />
                  </Button>
                </DropdownMenuTrigger>

                <DropdownMenuContent>

                  <DropdownMenuItem onClick={() => navigate(`/items/${item.id}/edit`)}>
                    <Pencil size={14} className="mr-2" /> Edit
                  </DropdownMenuItem>

                  <DropdownMenuItem onClick={() => handleCopy(item)}>
                    <Copy size={14} className="mr-2" /> Copy Item
                  </DropdownMenuItem>

                  <DropdownMenuItem
                    className="text-red-600"
                    onClick={() => handleDelete(item)}
                  >
                    <Trash2 size={14} className="mr-2" /> Delete
                  </DropdownMenuItem>

                </DropdownMenuContent>

              </DropdownMenu>

            </CardContent>
          </Card>

        ))}

      </div>

    </div>
  );

};

export default ItemsPage;